import { useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
  type TooltipValueType,
} from 'recharts';
import { formatCompact, formatDay, formatNumber } from '../lib/format';
import { useT } from '../lib/i18n';
import type { TrendPoint } from '../lib/types';
import { Button, cn } from './ui';

export interface SeriesSpec {
  key: keyof TrendPoint;
  label: string;
  /** A `--series-N` token. Assigned in fixed order, never cycled. */
  color: string;
}

/**
 * Two or three counts over time, as lines.
 *
 * Lines because the job is change over time with identity by hue, and one
 * y-axis because every series on a given chart counts the same kind of thing.
 * A second scale is the single most misleading thing a chart like this can do:
 * it would let ten payouts and ten thousand rings occupy the same visual
 * height, and no amount of axis labelling undoes that impression.
 *
 * Identity is never colour alone — the legend pairs each swatch with a text
 * label in ink tokens, and the "Show data" view is the table fallback the
 * palette's light-mode contrast obliges.
 */
export function TrendChart({
  data,
  series,
  height = 'h-64 sm:h-72',
}: {
  data: TrendPoint[];
  series: SeriesSpec[];
  height?: string;
}) {
  const t = useT();
  const [showTable, setShowTable] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Legend series={series} />
        <Button
          variant="ghost"
          className="text-xs"
          onClick={() => setShowTable((current) => !current)}
          aria-expanded={showTable}
        >
          {showTable ? t('chart.showChart') : t('chart.showData')}
        </Button>
      </div>

      {showTable ? (
        <DataView data={data} series={series} />
      ) : (
        <div className={cn('w-full', height)}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 8, right: 12, bottom: 0, left: -18 }}
            >
              {/* Recessive chrome: horizontal hairlines only, no vertical rules. */}
              <CartesianGrid
                horizontal
                vertical={false}
                stroke="var(--grid)"
                strokeWidth={1}
              />
              <XAxis
                dataKey="date"
                tickFormatter={formatDay}
                tickLine={false}
                axisLine={{ stroke: 'var(--line)' }}
                tick={{ fill: 'var(--ink-3)', fontSize: 11 }}
                minTickGap={24}
              />
              <YAxis
                tickFormatter={formatCompact}
                tickLine={false}
                axisLine={false}
                tick={{ fill: 'var(--ink-3)', fontSize: 11 }}
                width={56}
                allowDecimals={false}
              />
              <Tooltip
                content={(props) => <ChartTooltip {...props} />}
                cursor={{ stroke: 'var(--line-strong)', strokeWidth: 1 }}
              />
              {series.map((spec) => (
                <Line
                  key={String(spec.key)}
                  type="monotone"
                  dataKey={spec.key}
                  name={spec.label}
                  stroke={spec.color}
                  strokeWidth={2}
                  dot={false}
                  // Comfortably larger than the line, with a surface ring so
                  // two series stay separable where they cross.
                  activeDot={{
                    r: 4.5,
                    strokeWidth: 2,
                    stroke: 'var(--surface)',
                  }}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function Legend({ series }: { series: SeriesSpec[] }) {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
      {series.map((spec) => (
        <li key={String(spec.key)} className="flex items-center gap-1.5 text-xs">
          <span
            aria-hidden
            className="h-0.5 w-4 rounded-full"
            style={{ backgroundColor: spec.color }}
          />
          <span className="text-ink-2">{spec.label}</span>
        </li>
      ))}
    </ul>
  );
}

function ChartTooltip({
  active,
  payload,
  label,
  // Recharts' own generics: narrowing either one makes the component
  // unassignable to `<Tooltip content>`.
}: TooltipContentProps<TooltipValueType, string | number>) {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-lg border border-line bg-surface px-3 py-2 shadow-lg">
      <p className="mb-1.5 text-xs font-medium text-ink">
        {formatDay(String(label))}
      </p>
      <ul className="space-y-1">
        {payload.map((entry) => (
          <li
            key={String(entry.dataKey)}
            className="flex items-center gap-2 text-xs whitespace-nowrap"
          >
            <span
              aria-hidden
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-ink-2">{entry.name}</span>
            <span className="tnum ml-auto font-medium text-ink">
              {formatNumber(Number(entry.value ?? 0))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The same numbers as a table — the accessible fallback for the chart. */
function DataView({
  data,
  series,
}: {
  data: TrendPoint[];
  series: SeriesSpec[];
}) {
  const t = useT();

  return (
    <div className="max-h-72 overflow-auto rounded-lg border border-line">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-raised text-xs text-ink-2">
          <tr>
            <th scope="col" className="px-3 py-2 text-left font-medium">
              {t('chart.date')}
            </th>
            {series.map((spec) => (
              <th
                key={String(spec.key)}
                scope="col"
                className="px-3 py-2 text-right font-medium"
              >
                {spec.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {data.map((point) => (
            <tr key={point.date}>
              <td className="px-3 py-1.5 text-ink-2">{formatDay(point.date)}</td>
              {series.map((spec) => (
                <td
                  key={String(spec.key)}
                  className="tnum px-3 py-1.5 text-right text-ink"
                >
                  {formatNumber(Number(point[spec.key] ?? 0))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
